// src/components/DiseasePanel.tsx (疾患深掘り Phase 1)
// レイアウト:
//   ① ヘッダ: タイトル + 期間 + 適用 filter + 粗利定義の脚注
//   ② DiseaseRankingTable
//   ③ 警告 (重症度補正未実施)
//
// 期間: asOfMonth 基準で 直近 12 ヶ月 (月初〜月末)。 useDiseaseRanking 内で計算。
//
// 初回 mount 時 (= store default で dpcfy=2024 起動するが dateFromOverride/To が
// まだ null の状態) に /api/query/disease_period_range を 1 回 fetch して
// 「2024 年度 range 内 直近 12 ヶ月」 を override にセットする。
import { useEffect } from "react";
import { useFilters } from "../store";
import { useAuth } from "../lib/useAuth";
import { useDiseaseRanking, diseasePeriod } from "../lib/useDiseaseRanking";
import { DpcTopBar } from "./DpcTopBar";
import { DiseaseRankingTable } from "./DiseaseRankingTable";
import { KcodeTablesRow } from "./KcodeTablesRow";
import { IcdRankingTable } from "./IcdRankingTable";
import { SelectedDiseaseDetail } from "./SelectedDiseaseDetail";
import { DpcTensuPanel } from "./DpcTensuPanel";
import { IS_THETA, gpText } from "../lib/gpMode";

export function DiseasePanel() {
  const f = useFilters();
  const { canViewDoctor } = useAuth();
  // 表示期間はデータ取得 hook と同じ実効期間 (override 優先) を使う。
  // サイドバーは dateFromOverride/dateToOverride を設定するので、ここを揃えないと
  // ラベルの「直近期間」がサイドバー選択とずれる (2026-06-18 修正)。
  const { dateFrom: defFrom, dateTo: defTo } = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const dateFrom = f.dateFromOverride ?? defFrom;
  const dateTo   = f.dateToOverride   ?? defTo;
  // [期間表示 2026-06-26] "2026-05-01" → "26年05月"(見切れ対策)。同月なら範囲を畳む。
  // [2026-07-17] 月はゼロ埋め 2 桁に統一 (ユーザ指示: 「2025年05月」→「25年05月」)。
  const ymShort = (d?: string) => {
    if (!d) return "";
    const p = d.split("-");
    return `${p[0].slice(2)}年${p[1]}月`;
  };
  const periodShort = (() => {
    const a = ymShort(dateFrom), b = ymShort(dateTo);
    return a === b ? a : `${a}〜${b}`;
  })();
  const { data } = useDiseaseRanking();

  // 自動期間は手動overrideと分け、URLへ固定保存しない。
  // dpcfy != null かつ override 未設定なら、 endpoint で「range 内のデータある直近 12 ヶ月」を
  // 自動計算して set (range 内データが 1 ヶ月しかなければ 開始=終了 のその月に収束。
  // 例: 2026 年度でデータが 2026-06 のみ → 26年06月〜26年06月)。
  // [2026-07-17] 初回 mount 限定 → 「dpcfy 変更 / 期間リセットで override が null に戻るたび」に一般化。
  //   改定年度セレクタ (FilterBar) 側の inline fetch はここに一元化した。
  useEffect(() => {
    if (f.dpcfy == null) return;
    if (f.dateFromOverride != null || f.dateToOverride != null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/query/disease_period_range?dpcfy=${f.dpcfy}`,
          { credentials: "include" },
        );
        if (!r.ok) throw new Error(`disease_period_range ${r.status}`);
        const d = await r.json() as {
          dataFrom: string | null; dataTo: string | null;
          rangeStart: string; hasData: boolean;
        };
        if (cancelled) return;
        // 注: setDateFromOverride / setDateToOverride は副作用で dpc6 / drill-down path を
        // クリアするため、 range 自動設定では useFilters.setState を直接呼んで
        // URL から復元した dpc6 等を保持する (= URL シェア互換)。
        if (d.hasData && d.dataFrom && d.dataTo) {
          useFilters.setState({ diseaseAutoPeriod: {dpcfy: f.dpcfy!, dateFrom: d.dataFrom, dateTo: d.dataTo} });
        } else {
          useFilters.setState({ diseaseAutoPeriod: {dpcfy: f.dpcfy!, dateFrom: d.rangeStart, dateTo: d.rangeStart} });
        }
      } catch (e) {
        console.error("disease_period_range init error:", e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.dpcfy, f.dateFromOverride, f.dateToOverride]);

  // range 内データ無し (例: 2026 年度開始前) banner 表示判定:
  //   dpcfy が指定済 + ranking 結果が 0 件 + 実効月数が 1 (= 0 件期間の division-by-zero 回避値)
  const showNoDataBanner =
    f.dpcfy != null &&
    !!data &&
    data.totalCases === 0 &&
    data.monthsEffective === 1;

  return (
    <div className="space-y-3">
      <section className="card p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-semibold">
            🔬 DPC分析（急性期・退院患者ベース）
          </h2>
          {data?.months ? (
            <span className="text-base font-semibold text-emerald-700">
              {data.months} ヶ月{f.dpcAgg === "月平均" ? "平均" : "累計"}
            </span>
          ) : null}
          <span className="perf-pill">
            {periodShort}
          </span>
          {f.focusedDept && (
            <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200"
                  title="退院時診療科（急性期最終日の診療科）で絞り込み">
              退院時診療科: {f.focusedDept}
            </span>
          )}
          {f.dpc6 && (
            <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">
              DPC 6: {f.dpc6}
            </span>
          )}
          {f.admdrcd && canViewDoctor && (
            <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">
              医師: {f.admdrcd}
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 mt-1 leading-tight">
          直近期間: {periodShort} ({data?.months ?? 12} ヶ月)
        </div>
        {/* [中央値 2026-07-22 ユーザ指示] 表の1日単価は「患者1人ずつの単価の中央値」に変更。
            下のサマリ行(病院全体の1日単価)は期間全体の加重平均のままなので、両者を書き分ける。 */}
        <div className="text-xs text-slate-500 mt-2 leading-tight">
          {IS_THETA
            ? "GP = 急性期の収益 − 包括前薬剤費 − 包括前材料費 − 医師人件費。"
            : "粗利 = 急性期の収益 − 包括前薬剤費 − 包括前材料費。"}
          入院日数 = 急性期最終日 − 入院日 + 1。<br />
          <b className="text-emerald-700">{gpText("※ 下の表の 1日粗利単価 / 1日入院単価 は「患者1人ずつの1日単価」の中央値")}</b>
          （長期入院の外れ値に引っ張られないようにするため。合計÷合計ではありません）。
        </div>
        {data?.summary && (() => {
          // 2026-05-28: 期間内集計 → 月平均表示 (= ユーザ指示)。
          // 分母は monthsEffective (= 行為別集計がある月数、 0 件月は除外)。
          // [2026-09-28 ユーザ指示] サイドバー「件数・金額の表示」に従う (既定=期間累計なら割らない)。
          const m = f.dpcAgg === "月平均" ? Math.max(1, data.monthsEffective ?? data.months ?? 1) : 1;
          // 2026-05-28 ユーザ指示: 月平均は整数表示 (小数点不要)。
          const fmt = (v: number) => Math.round(v).toLocaleString();
          // 単価 (= 期間累計の 1 日単価、 月平均ではない)。
          // 分母 = los_total (= 急性期入院延日数 = Σ(急性期最終日−入院日+1))、 0 時は表示せず。
          const losTotal = data.summary.los_total;
          const pricePerDay = losTotal > 0 ? Math.round(data.summary.revenue_total / losTotal) : null;
          const gpPerDay    = losTotal > 0 ? Math.round(data.summary.gp_total      / losTotal) : null;
          return (
            <div className="text-xs text-slate-700 mt-2 leading-tight">
              <span className="text-emerald-700 font-semibold">急性期退院ベース</span>{" "}
              <b>{f.dpcAgg === "月平均" ? "月平均" : "期間累計"}:</b>{" "}
              急性期退院 <b>{m > 1 ? (data.summary.n_cases_total / m).toFixed(1) : fmt(data.summary.n_cases_total)}</b> 件 /{" "}
              入院延日数 <b>{fmt(data.summary.los_total / m)}</b> 日 /{" "}
              入院収入 <b>{fmt(data.summary.revenue_total / 1000 / m)}</b> 千円 /{" "}
              {gpText("粗利")} <b>{fmt(data.summary.gp_total / 1000 / m)}</b> 千円{" "}
              | 入院日数中央値 <b>{Math.round(data.summary.los_median)}</b> 日<span className="text-slate-400">(中央値)</span>
              {pricePerDay !== null && (
                <span className="ml-2 text-slate-600"
                      title={gpText("このサマリ行は期間全体の加重平均 (Σ収入 or Σ粗利 ÷ Σ入院延日数)。表の各行の 1日単価 (患者1人ずつの中央値) とは定義が異なります。")}>
                  | 1日入院単価 <b>{pricePerDay.toLocaleString()}</b> 円 /{" "}
                  {gpText("1日粗利単価")} <b>{gpPerDay!.toLocaleString()}</b> 円
                  <span className="text-slate-400">(全体・合計÷合計)</span>
                </span>
              )}
            </div>
          );
        })()}
      </section>

      {showNoDataBanner && (
        <section className="card p-3 bg-sky-50 border border-sky-200">
          <div className="text-xs text-sky-800 leading-relaxed">
            <b>ℹ {f.dpcfy} 年度のデータがまだありません。</b>
            選択中の年度 (= {f.dpcfy === 2024 ? "2024-06" : "2026-06"} 〜) の
            range 内に 収益確定済の症例が存在しません。
            DPC 年度を切り替えるか、 全期間に戻してください。
          </div>
        </section>
      )}

      {/* [急性期ベース 2026-06-26] 科別/医師別ランキング(表0/0.5)は廃止。
          科別があると入院日数(中央値/延日数)の概念がぶれるため。
          診療科はサイドバーの絞り込み(退院時診療科=急性期最終日の科)にのみ使う。 */}
      {/* [drill-down廃止 2026-06-27] 表2-5 accordion を廃止。主表のレベル切替(6/10/11/12)で
          全体俯瞰し、行クリックで selectedDpcN をセット → 下の表6/7/8・DPC包括点数を絞る。 */}
      {/* [2026-07-01] 6~14桁の切替タブを表の直上に(その他フィルタは折りたたみ可能なサイドバー) */}
      <DpcTopBar />

      <DiseaseRankingTable />

      {/* [2026-06-27 デザイン改善] 選択行の文脈バー(連動の発見性+副傷病/重症度を常時表示+解除)。
          主表(クリック起点)と下の絞り込み表群の間に置き、「ここで選ぶと下が変わる」を明示する。
          [印刷 2026-07-22] 印刷では下の ICD/実績K を出さないので、この案内も出さない
          (「下の表が絞り込まれます」だけが紙に残るのを防ぐ)。絞り込み中の疾患は
          ヘッダの「DPC 6: xxxxxx」pill と主表の 🎯 で分かる。 */}
      <div className="no-print">
        <SelectedDiseaseDetail />
      </div>

      {/* [ICD表 2026-06-26] ICD・病名別 急性期退院数(期間累計)の左に、実績手術・処置Kコード。
          いずれも選択中の疾患/区分・共通 filter に連動。 */}
      {/* [印刷 2026-07-22 ユーザ指示] ICD別以降は印刷不要 (印刷は主表までで完結させる) */}
      <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-2 items-start no-print">
        <IcdRankingTable />
        <KcodeTablesRow />
      </div>

      {/* [2026-06-27 ユーザ指示] DPC包括点数(DPC期間ごとの点数)は 14桁(完全)レベルの時だけ表示。
          14桁=特定DPCが定まる粒度なので、選択した14桁から階段チャート+期間別点数表+包括金額試算を出す。 */}
      {f.dpcLevel === 14 && <div className="no-print"><DpcTensuPanel /></div>}

    </div>
  );
}
